/**
 * GA4 Event Tracking Constants and Types
 *
 * Naming Conventions:
 * - snake_case only (GA4 is case-sensitive)
 * - verb_object pattern (e.g., cta_click, form_submit)
 * - Location/context in parameters, NOT event names
 */

// =============================================================================
// Event Names
// =============================================================================

export const EVENTS = {
  // Engagement events
  CTA_CLICK: "cta_click",
  PAGE_VIEW: "page_view",

  // Form events
  FORM_START: "form_start",
  FORM_SUBMIT: "form_submit",

  // Lead & conversion events
  GENERATE_LEAD: "generate_lead",
  NEWSLETTER_SIGNUP: "newsletter_signup",

  // Checkout & purchase events
  BEGIN_CHECKOUT: "begin_checkout",
  ADD_PAYMENT_INFO: "add_payment_info",
  PURCHASE: "purchase",

  // Product-specific events
  UPGRADE_VIEW: "upgrade_view",
  ANALYSIS_START: "analysis_start",
  ANALYSIS_COMPLETE: "analysis_complete",

  // Share events
  SHARE: "share",

  // Chat events (product-specific)
  CHAT_START: "chat_start",
  CHAT_MESSAGE_SEND: "chat_message_send",
  CHAT_SHARE: "chat_share",

  // Auth events
  SIGN_UP: "sign_up",
  LOGIN: "login",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

// =============================================================================
// Placements - Where on the site the action occurred
// =============================================================================

export const PLACEMENTS = {
  HERO: "hero",
  HEADER: "header",
  FOOTER: "footer",
  SIDEBAR: "sidebar",
  MODAL: "modal",
  PRICING: "pricing",
  CHECKOUT: "checkout",
  CHAT: "chat",
  LANDING: "landing",
  SETTINGS: "settings",
  SHARE_MODAL: "share_modal",
} as const;

export type Placement = (typeof PLACEMENTS)[keyof typeof PLACEMENTS];

// =============================================================================
// CTA IDs - Stable identifiers for CTAs (won't change with UI text)
// =============================================================================

export const CTA_IDS = {
  // Header CTAs
  NAV_GET_STARTED: "nav_get_started",
  NAV_LOGIN: "nav_login",
  NAV_SIGN_UP: "nav_sign_up",
  NAV_FEATURES: "nav_features",
  NAV_CHAINS: "nav_chains",
  NAV_ABOUT: "nav_about",

  // Hero CTAs
  HERO_PRIMARY: "hero_primary",
  HERO_SECONDARY: "hero_secondary",
  HERO_TRY_EXAMPLE: "hero_try_example",

  // Landing Page CTAs
  LANDING_LAUNCH: "landing_launch",
  LANDING_CONTACT: "landing_contact",
  LANDING_TWITTER: "landing_twitter",
  LANDING_GITHUB: "landing_github",

  // Pricing CTAs
  PRICING_FREE: "pricing_free",
  PRICING_PRO: "pricing_pro",
  PRICING_ENTERPRISE: "pricing_enterprise",

  // Footer CTAs
  FOOTER_CTA: "footer_cta",
  FOOTER_NEWSLETTER: "footer_newsletter",

  // Modal CTAs
  MODAL_CONFIRM: "modal_confirm",
  MODAL_CANCEL: "modal_cancel",
  MODAL_UPGRADE: "modal_upgrade",

  // Chat CTAs
  CHAT_NEW: "chat_new",
  CHAT_SHARE: "chat_share",
  CHAT_COPY: "chat_copy",
  CHAT_SEND: "chat_send",
  CHAT_SUGGESTED: "chat_suggested",

  // Sidebar CTAs
  SIDEBAR_NEW_CHAT: "sidebar_new_chat",
  SIDEBAR_UPGRADE: "sidebar_upgrade",
  SIDEBAR_CHAT_SELECT: "sidebar_chat_select",
  SIDEBAR_DELETE_CHAT: "sidebar_delete_chat",
  SIDEBAR_DELETE_ALL: "sidebar_delete_all",

  // Share CTAs
  SHARE_TWITTER: "share_twitter",
  SHARE_TELEGRAM: "share_telegram",
  SHARE_COPY_LINK: "share_copy_link",
  SHARE_NATIVE: "share_native",

  // Message actions
  MESSAGE_COPY: "message_copy",
  MESSAGE_EDIT: "message_edit",
  MESSAGE_UPVOTE: "message_upvote",
  MESSAGE_DOWNVOTE: "message_downvote",

  // Visibility
  VISIBILITY_PRIVATE: "visibility_private",
  VISIBILITY_PUBLIC: "visibility_public",

  // Auth
  AUTH_SUBMIT: "auth_submit",
  AUTH_SWITCH: "auth_switch",
} as const;

export type CtaId = (typeof CTA_IDS)[keyof typeof CTA_IDS];

// =============================================================================
// Form Types
// =============================================================================

export const FORM_TYPES = {
  EMAIL_CAPTURE: "email_capture",
  NEWSLETTER: "newsletter",
  LOGIN: "login",
  REGISTER: "register",
  CHECKOUT: "checkout",
  CONTACT: "contact",
  FEEDBACK: "feedback",
  SEARCH: "search",
  CHAT_INPUT: "chat_input",
} as const;

export type FormType = (typeof FORM_TYPES)[keyof typeof FORM_TYPES];

// =============================================================================
// Tiers
// =============================================================================

export const TIERS = {
  FREE: "free",
  PRO: "pro",
  ENTERPRISE: "enterprise",
} as const;

export type Tier = (typeof TIERS)[keyof typeof TIERS];

// =============================================================================
// Share Platforms
// =============================================================================

export const SHARE_PLATFORMS = {
  TWITTER: "twitter",
  TELEGRAM: "telegram",
  COPY: "copy",
  NATIVE: "native",
} as const;

export type SharePlatform =
  (typeof SHARE_PLATFORMS)[keyof typeof SHARE_PLATFORMS];

// =============================================================================
// Auth Methods
// =============================================================================

export const AUTH_METHODS = {
  EMAIL: "email",
  GOOGLE: "google",
  GITHUB: "github",
  GUEST: "guest",
} as const;

export type AuthMethod = (typeof AUTH_METHODS)[keyof typeof AUTH_METHODS];

// =============================================================================
// Event Parameter Interfaces
// =============================================================================

export type BaseEventParams = {
  placement?: Placement | string;
};

export type CtaClickParams = BaseEventParams & {
  cta_id: CtaId | string;
  placement: Placement | string;
  tier?: Tier | string;
  destination?: string;
};

export type FormStartParams = BaseEventParams & {
  form_id: string;
  form_type: FormType | string;
  placement: Placement | string;
};

export type FormSubmitParams = BaseEventParams & {
  form_id: string;
  form_type: FormType | string;
  placement: Placement | string;
  success: boolean;
};

export type GenerateLeadParams = BaseEventParams & {
  lead_type: string;
  email_domain?: string;
  method?: string;
};

export type NewsletterSignupParams = BaseEventParams & {
  email_domain?: string;
  method?: string;
};

export type BeginCheckoutParams = BaseEventParams & {
  value: number;
  currency: string;
  tier: Tier | string;
  domain?: string;
};

export type AddPaymentInfoParams = BaseEventParams & {
  value: number;
  currency: string;
  tier: Tier | string;
  payment_method: string;
};

export type PurchaseParams = BaseEventParams & {
  transaction_id: string;
  value: number;
  currency: string;
  tier: Tier | string;
};

export type UpgradeViewParams = BaseEventParams & {
  current_tier: Tier | string;
  suggested_tier: Tier | string;
  trigger?: string;
};

export type AnalysisStartParams = BaseEventParams & {
  analysis_type?: string;
  chain?: string;
  query_type?: string;
};

export type AnalysisCompleteParams = BaseEventParams & {
  analysis_type?: string;
  chain?: string;
  query_type?: string;
  success: boolean;
  duration_ms?: number;
};

export type ShareParams = BaseEventParams & {
  platform: SharePlatform | string;
  content_type: string;
  content_id?: string;
};

export type ChatStartParams = BaseEventParams & {
  chat_id?: string;
  is_guest: boolean;
};

export type ChatMessageSendParams = BaseEventParams & {
  chat_id: string;
  message_length: number;
  has_attachments?: boolean;
};

export type ChatShareParams = BaseEventParams & {
  chat_id: string;
  platform: SharePlatform | string;
  message_count?: number;
};

export type SignUpParams = BaseEventParams & {
  method: AuthMethod | string;
};

export type LoginParams = BaseEventParams & {
  method: AuthMethod | string;
};
